import { useState, useEffect } from "react";
import { supabase } from "@/libs/supabase";
import type { WeeklyProgress } from "@/types/progress";

interface UseWeeklyProgressReturn {
	weeklyProgress: WeeklyProgress[];
	loading: boolean;
	error: Error | null;
}

export const useWeeklyProgress = (
	goalIds: string[],
): UseWeeklyProgressReturn => {
	const [weeklyProgress, setWeeklyProgress] = useState<WeeklyProgress[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		const fetchWeeklyProgress = async () => {
			if (!goalIds.length) {
				setWeeklyProgress([]);
				setLoading(false);
				return;
			}

			try {
				// 現在の日付から過去7日間の日付範囲を取得
				const today = new Date();
				const sevenDaysAgo = new Date(today);
				sevenDaysAgo.setDate(today.getDate() - 6); // 今日を含めて7日間

				// 日付をYYYY-MM-DD形式に変換
				const startDate = sevenDaysAgo.toISOString().split("T")[0];
				const endDate = today.toISOString().split("T")[0];

				// 進捗データを取得
				const { data, error } = await supabase
					.from("progress_logs")
					.select("*")
					.in("goal_id", goalIds)
					.gte("created_at", startDate)
					.lte("created_at", endDate)
					.order("created_at", { ascending: true });

				if (error) throw error;

				// 各目標の週次データを集計
				const weeklyData = goalIds.map((goalId) => {
					const progressArray = Array(7).fill(0);
					const goalLogs = data.filter((log) => log.goal_id === goalId);

					// 各日の最新の進捗を取得
					for (let i = 0; i < 7; i++) {
						const targetDate = new Date(sevenDaysAgo);
						targetDate.setDate(sevenDaysAgo.getDate() + i);
						const dateStr = targetDate.toISOString().split("T")[0];

						// その日の進捗ログを取得
						const dayLogs = goalLogs.filter(
							(log) => log.created_at.split("T")[0] === dateStr,
						);

						if (dayLogs.length > 0) {
							// その日の最新の進捗を使用
							progressArray[i] = dayLogs[dayLogs.length - 1].progress;
						} else if (i > 0) {
							// データがない場合は前日の値を使用
							progressArray[i] = progressArray[i - 1];
						}
					}

					return {
						goalId,
						weeklyData: progressArray,
					};
				});

				setWeeklyProgress(weeklyData);
			} catch (err) {
				console.error("週次進捗データの取得に失敗しました:", err);
				setError(err as Error);
			} finally {
				setLoading(false);
			}
		};

		fetchWeeklyProgress();

		// リアルタイム更新のサブスクリプション
		const subscription = supabase
			.channel("weekly_progress_changes")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "progress_logs",
					filter: `goal_id=in.(${goalIds.join(",")})`,
				},
				() => {
					fetchWeeklyProgress();
				},
			)
			.subscribe();

		return () => {
			subscription.unsubscribe();
		};
	}, [goalIds]);

	return { weeklyProgress, loading, error };
};
